import { h, Component, Prop, Element, Build, Watch } from '@stencil/core';

import * as am5 from '@amcharts/amcharts5/index';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const generateMockData = (length: number) => {
  const countries = [
    'USA',
    'China',
    'Japan',
    'Germany',
    'UK',
    'Frandce',
    'India',
    'Spain',
    'Netherlands',
    'South Korea',
    'Guatemala',
    'Peru',
    'Brazil',
    'Honduras',
    'Hungary',
    'Romania',
    'North Korea',
    'Indonesia',
    'Australia',
    'Ukraine',
  ];
  return countries.map(c => ({ label: c, value: Math.random() * 2000 })).slice(0, length);
};
@Component({
  tag: 'udp-donut-chart',
  styleUrl: 'udp-donut-chart.css',
  shadow: true,
})
export class UdpDonutChart {
  @Element() el: HTMLElement;

  @Prop() data: { [key: string]: any }[] = Build.isDev ? generateMockData(10) : undefined;
  @Prop() valueField: string = Build.isDev ? 'value' : '';
  @Prop() labelField: string = Build.isDev ? 'label' : '';
  @Prop() title: string = Build.isDev ? 'Test header' : undefined;
  @Prop() subheader: string = Build.isDev ? 'Test SubHeader' : undefined;
  @Prop() hideLegend: boolean = false;
  @Prop() size: 'small' | 'medium' | 'large' = 'medium';
  @Prop() customWidth?: number; // Rather then setting a size you can set a custom width
  @Prop() customHeight?: number; // Rather then setting a size you can set a custom height
  @Prop() customCardWidth?: number; // Rather then setting a size you can set a custom card width
  @Prop() customCardStyle?: {[key: string]: string} = {};
  @Prop() kpisPosition: 'left' | 'top' = 'left';
  @Prop() kpis: { value: string; label: string }[] = Build.isDev
    ? [
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
        { value: Math.round(Math.random() * 2000).toString(), label: 'YTD' },
      ]
    : undefined;
  @Prop() hideAdornment: boolean = false;

  private chartId = 'udp-donut-chart';
  private chartRoot: am5.Root;

  @Watch('valueField')
  @Watch('labelField')
  @Watch('hideLegend')
  @Watch('data')
  async renderChart() {
    // sometimes the element cannot be found right away so loop umtil it loads
    if (this.el.shadowRoot.getElementById(this.chartId) === null) {
      await setTimeout(this.renderChart, 50);
    }
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
    if (!this.valueField) return;

    this.chartRoot = am5.Root.new(this.el.shadowRoot.getElementById(this.chartId));

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    this.chartRoot.setThemes([am5themes_Animated.new(this.chartRoot)]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/
    let chart = this.chartRoot.container.children.push(
      am5percent.PieChart.new(this.chartRoot, {
        layout: this.chartRoot.verticalLayout,
        innerRadius: am5.percent(65),
      }),
    );

    // Create series
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Series
    let series = chart.series.push(
      am5percent.PieSeries.new(this.chartRoot, {
        valueField: Build.isDev ? 'value' : this.valueField,
        categoryField: Build.isDev ? 'label' : this.labelField,
        alignLabels: false,
      }),
    );

    series.labels.template.setAll({
      textType: 'circular',
      centerX: 0,
      centerY: 0,
      text: `{${this.valueField}}`,
      tooltipText: `{${this.labelField}}: {${this.valueField}}`,
      oversizedBehavior: 'truncate',
      maxWidth: 100,
      maxHeight: 100,
      ellipsis: '...',
    });
    series.labels.template.adapters.add('y', function (y, target) {
      let dataItem = target.dataItem;
      if (dataItem) {
        //@ts-ignore
        let tick = dataItem.get('tick');
        if (tick) {
          //@ts-ignore
          if ((dataItem.get('valuePercentTotal') as number) < 5) {
            target.set('forceHidden', true);
            //@ts-ignore
            tick.set('forceHidden', true);
          } else {
            target.set('forceHidden', false);
            //@ts-ignore
            tick.set('forceHidden', false);
          }
        }
        return y;
      }
    });

    // Set data
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Setting_data
    series.data.setAll(this.data);

    // Create legend
    // https://www.amcharts.com/docs/v5/charts/percent-charts/legend-percent-series/
    let legend =
      this.size !== 'small' && !this.hideLegend
        ? chart.children.push(
            am5.Legend.new(this.chartRoot, {
              centerX: am5.percent(50),
              x: am5.percent(50),
              marginTop: 15,
              marginBottom: 15,
            }),
          )
        : null;

    legend?.data.setAll(series.dataItems);
    if (legend && this.size !== 'small') {
      // Set maxWidth for labels within the legend items
      legend.labels.template.setAll({
        maxWidth: this.chartRoot.width() * 0.24,
        oversizedBehavior: 'truncate', // Ensures text wraps if it exceeds maxWidth
      });
    }

    // Play initial series animation
    // https://www.amcharts.com/docs/v5/concepts/animations/#Animation_of_series
    series.appear(1000, 100);
  }

  componentDidLoad() {
    this.renderChart();
  }

  disconnectedCallback() {
    // Clean up chart instance on component removal
    if (this.chartRoot) {
      this.chartRoot.dispose();
    }
  }
  render() {
    return (
      <chart-container
        kpis={this.kpis}
        chartId={this.chartId}
        title={this.title}
        subheader={this.subheader}
        size={this.size}
        customWidth={this.customWidth}
        customHeight={this.customHeight}
        customCardWidth={this.customCardWidth}
        customCardStyle={this.customCardStyle}
        kpisPosition={this.kpisPosition}
        hideAdornment={this.hideAdornment}
      />
    );
  }
}
